import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Typography,
  Box,
  Divider,
  Chip,
  Menu,
  MenuItem,
  Tooltip,
  alpha,
  TextField,
  InputAdornment,
  Button,
} from "@mui/material";
import {
  WorkOutline,
  ExpandMore,
  ChevronRight,
  Add as AddIcon,
  Settings as SettingsIcon,
  Dashboard as DashboardIcon,
  BugReport,
  BarChart,
  Group,
  Search,
  Person,
  FiberManualRecord,
  Star as StarIcon,
  MoreHoriz as MoreHorizIcon,
  Close as CloseIcon,
  CreateNewFolder as CreateNewFolderIcon,
  Folder as FolderIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import {
  toggleSearch,
  setSearchQuery,
  addFavorite,
  FavoriteItem
} from '../../store/favoritesSlice';
import { fetchWorkspaces } from '../../store/workspaceSlice';
import CreateWorkspaceModal from '../../components/Workspace/CreateWorkspaceModal';
import EditWorkspaceModal from '../../components/Workspace/EditWorkspaceModal';
import "./Sidebar.scss";
import { Workspace } from '../../types/common';

interface WorkspaceProps {
  name: string;
  color: string;
  boards: BoardItem[];
}

interface BoardItem {
  name: string;
  route?: string;
}

// Custom hook for sidebar state management
const useSidebarState = () => {
  const [favoritesMenuAnchorEl, setFavoritesMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [workspaceMenuAnchorEl, setWorkspaceMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [expandedWorkspaces, setExpandedWorkspaces] = useState<{
    [key: string]: boolean;
  }>({
    development: true,
  });
  const [isFavoritesExpanded, setIsFavoritesExpanded] = useState(true);
  const [isWorkspaceSearchActive, setIsWorkspaceSearchActive] = useState(false);
  const [workspaceSearchQuery, setWorkspaceSearchQuery] = useState('');
  const [isCreateWorkspaceModalOpen, setIsCreateWorkspaceModalOpen] = useState(false);
  const [isEditWorkspaceModalOpen, setIsEditWorkspaceModalOpen] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(null);

  return {
    favoritesMenuAnchorEl,
    setFavoritesMenuAnchorEl,
    workspaceMenuAnchorEl,
    setWorkspaceMenuAnchorEl,
    expandedWorkspaces,
    setExpandedWorkspaces,
    isFavoritesExpanded,
    setIsFavoritesExpanded,
    isWorkspaceSearchActive,
    setIsWorkspaceSearchActive,
    workspaceSearchQuery,
    setWorkspaceSearchQuery,
    isCreateWorkspaceModalOpen,
    setIsCreateWorkspaceModalOpen,
    isEditWorkspaceModalOpen,
    setIsEditWorkspaceModalOpen,
    selectedWorkspace,
    setSelectedWorkspace,
  };
};

export const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  // Redux state
  const { items: favorites, searchQuery, isSearchActive } = useAppSelector((state) => state.favorites);
  const { workspaces } = useAppSelector((state) => state.workspaces);
  const { boards: reduxBoards } = useAppSelector((state) => state.boards);
  
  // Local state management
  const {
    favoritesMenuAnchorEl,
    setFavoritesMenuAnchorEl,
    workspaceMenuAnchorEl,
    setWorkspaceMenuAnchorEl,
    expandedWorkspaces,
    setExpandedWorkspaces,
    isFavoritesExpanded,
    setIsFavoritesExpanded,
    isWorkspaceSearchActive,
    setIsWorkspaceSearchActive,
    workspaceSearchQuery,
    setWorkspaceSearchQuery,
    isCreateWorkspaceModalOpen,
    setIsCreateWorkspaceModalOpen,
    isEditWorkspaceModalOpen,
    setIsEditWorkspaceModalOpen,
    selectedWorkspace,
    setSelectedWorkspace,
  } = useSidebarState();

  const isActive = (path: string) => location.pathname === path;

  // Fetch workspaces on component mount
  useEffect(() => {
    dispatch(fetchWorkspaces());
  }, [dispatch]);

  // Workspace Modal Handlers
  const handleCreateWorkspaceModalOpen = useCallback(() => {
    setIsCreateWorkspaceModalOpen(true);
  }, [setIsCreateWorkspaceModalOpen]);

  const handleCreateWorkspaceModalClose = useCallback(() => {
    setIsCreateWorkspaceModalOpen(false);
  }, [setIsCreateWorkspaceModalOpen]);

  const handleEditWorkspaceModalOpen = useCallback((workspace: Workspace) => {
    setSelectedWorkspace(workspace);
    setIsEditWorkspaceModalOpen(true);
  }, [setSelectedWorkspace, setIsEditWorkspaceModalOpen]);

  const handleEditWorkspaceModalClose = useCallback(() => {
    setIsEditWorkspaceModalOpen(false);
    setSelectedWorkspace(null);
  }, [setIsEditWorkspaceModalOpen, setSelectedWorkspace]);

  // Favorites Handlers
  const handleSearchToggle = useCallback(() => {
    dispatch(toggleSearch());
    if (isSearchActive) {
      dispatch(setSearchQuery(''));
    }
  }, [dispatch, isSearchActive]);

  const handleSearchChange = useCallback((query: string) => {
    dispatch(setSearchQuery(query));
  }, [dispatch]);

  const handleCreateNewFolder = useCallback(() => {
    const folderName = prompt('Enter folder name:');
    if (folderName) {
      const newFolder: FavoriteItem = {
        id: Date.now().toString(),
        name: folderName,
        type: 'folder',
        icon: 'Folder',
        color: '#4b5563'
      };
      dispatch(addFavorite(newFolder));
    }
    setFavoritesMenuAnchorEl(null);
  }, [dispatch, setFavoritesMenuAnchorEl]);

  const handleAddToFavorites = useCallback(() => {
    const newFavorite: FavoriteItem = {
      id: Date.now().toString(),
      name: 'Current Board',
      type: 'board',
      icon: 'Dashboard',
      route: location.pathname,
      color: '#0073EA'
    };
    dispatch(addFavorite(newFavorite));
    setFavoritesMenuAnchorEl(null);
  }, [dispatch, location.pathname, setFavoritesMenuAnchorEl]);

  const handleFavoritesMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setFavoritesMenuAnchorEl(event.currentTarget);
  };

  const handleFavoritesMenuClose = useCallback(() => {
    setFavoritesMenuAnchorEl(null);
  }, [setFavoritesMenuAnchorEl]);

  // Toggle favorites collapse
  const handleFavoritesToggle = useCallback(() => {
    setIsFavoritesExpanded(!isFavoritesExpanded);
    if (isFavoritesExpanded && isSearchActive) {
      dispatch(toggleSearch());
      dispatch(setSearchQuery(''));
    }
  }, [isFavoritesExpanded, isSearchActive, dispatch, setIsFavoritesExpanded]);

  // Workspace Handlers
  const toggleWorkspace = useCallback((workspace: string) => {
    setExpandedWorkspaces((prev) => ({
      ...prev,
      [workspace]: !prev[workspace],
    }));
  }, [setExpandedWorkspaces]);

  const handleWorkspaceSearchToggle = useCallback(() => {
    setIsWorkspaceSearchActive(!isWorkspaceSearchActive);
    if (isWorkspaceSearchActive) {
      setWorkspaceSearchQuery('');
    }
  }, [isWorkspaceSearchActive, setIsWorkspaceSearchActive, setWorkspaceSearchQuery]);

  const handleWorkspaceSearchChange = useCallback((query: string) => {
    setWorkspaceSearchQuery(query);
  }, [setWorkspaceSearchQuery]);

  // THREE DOT MENU HANDLER - Workspace par click karne par
  const handleWorkspaceMenuOpen = (event: React.MouseEvent<HTMLElement>, workspace: Workspace) => {
    event.stopPropagation(); // Important: parent click event ko prevent karega
    setWorkspaceMenuAnchorEl(event.currentTarget);
    setSelectedWorkspace(workspace);
  };

  const handleWorkspaceMenuClose = useCallback(() => {
    setWorkspaceMenuAnchorEl(null);
    setSelectedWorkspace(null);
  }, [setWorkspaceMenuAnchorEl, setSelectedWorkspace]);

  const handleEditWorkspace = useCallback(() => {
    if (selectedWorkspace) {
      handleEditWorkspaceModalOpen(selectedWorkspace);
    }
    setWorkspaceMenuAnchorEl(null);
  }, [selectedWorkspace, handleEditWorkspaceModalOpen, setWorkspaceMenuAnchorEl]);

  const handleAddNewBoard = useCallback(() => {
    const boardName = prompt('Enter board name:');
    if (boardName) {
      console.log('New board:', boardName);
    }
    setWorkspaceMenuAnchorEl(null);
  }, [setWorkspaceMenuAnchorEl]);

  // Filter favorites based on search
  const filteredFavorites = (favorites || []).filter((fav: FavoriteItem) =>
    fav.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Transform Redux workspaces to match the existing structure
  const safeWorkspaces = workspaces || [];
  const transformedWorkspaces: WorkspaceProps[] = safeWorkspaces.length > 0
    ? safeWorkspaces.map(ws => {
        const wsBoardsFromRedux = (reduxBoards || []).filter(
          b => String(b.workspaceId) === String(ws.id)
        );

        const embeddedBoards = (ws.boards || []).map((b: any) => {
          if (typeof b === 'object' && b !== null) {
            return {
              id: String(b.id),
              name: b.name || `Board ${String(b.id || '').slice(-4)}`,
              route: `/boards/${b.id}`,
            };
          }
          return {
            id: String(b),
            name: `Board ${String(b).slice(-4)}`,
            route: `/boards/${b}`,
          };
        });

        const boardMap = new Map();
        embeddedBoards.forEach(b => boardMap.set(b.route, b));
        wsBoardsFromRedux.forEach(b => {
          const route = `/boards/${b.id}`;
          boardMap.set(route, {
            id: String(b.id),
            name: b.name || `Board ${String(b.id || '').slice(-4)}`,
            route,
          });
        });

        const finalBoards = Array.from(boardMap.values());

        return {
          name: ws.name || 'Unnamed Workspace',
          color: ws.color || '#4A90E2',
          boards: finalBoards,
        };
      })
    : [];

  // Filter workspaces based on search
  const filteredWorkspaces = transformedWorkspaces.filter(workspace =>
    workspace.name.toLowerCase().includes(workspaceSearchQuery.toLowerCase()) ||
    (workspace.boards || []).some(board =>
      board.name.toLowerCase().includes(workspaceSearchQuery.toLowerCase())
    )
  );

  // Get icon component based on icon name
  const getIconComponent = useCallback((iconName: string) => {
    const iconMap: { [key: string]: React.ReactElement } = {
      Dashboard: <DashboardIcon />,
      Group: <Group />,
      Person: <Person />,
      Folder: <FolderIcon />,
      BugReport: <BugReport />,
      BarChart: <BarChart />,
      Star: <StarIcon />
    };
    return iconMap[iconName] || <StarIcon />;
  }, []);

  return (
    <>
      <Drawer
        variant="permanent"
        className="mui-sidebar"
        sx={{
          width: 280,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: 280,
            boxSizing: "border-box",
            borderRight: "1px solid",
            borderColor: "divider",
            top: "64px",
            height: "calc(100vh - 64px)",
            background: "linear-gradient(180deg, #ffffff 0%, #f8f9fb 100%)",
            overflowX: "hidden",
          },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Favorites Section */}
          <Box sx={{ px: 2, mb: 2 }} className="favorites-section">
            {/* Favorites Header */}
            <Box className="favorites-header">
              <Box 
                sx={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: 1, 
                  cursor: "pointer",
                  flex: 1,
                  padding: "4px 8px",
                  borderRadius: "6px",
                  transition: "background-color 0.2s ease",
                  "&:hover": {
                    backgroundColor: alpha("#000", 0.03),
                  }
                }}
                onClick={handleFavoritesToggle}
              >
                {isFavoritesExpanded ? (
                  <ExpandMore sx={{ fontSize: 18, color: "#6b7280" }} />
                ) : (
                  <ChevronRight sx={{ fontSize: 18, color: "#6b7280" }} />
                )}
                <Typography
                  variant="caption"
                  sx={{
                    color: "#4b5563",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    fontSize: "0.6875rem",
                  }}
                >
                  Favorites
                </Typography>
              </Box>
              
              {/* Actions - Show only when expanded */}
              {isFavoritesExpanded && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }} className="favorites-actions">
                  <Tooltip title="Search favorites">
                    <IconButton
                      size="small"
                      onClick={handleSearchToggle}
                      className="action-btn"
                      sx={{
                        width: 24,
                        height: 24,
                        color: "#6b7280",
                        "&:hover": {
                          bgcolor: alpha("#0073ea", 0.08),
                          color: "#0073ea",
                        },
                      }}
                    >
                      <Search sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Favorites options">
                    <IconButton
                      size="small"
                      onClick={handleFavoritesMenuOpen}
                      className="action-btn"
                      sx={{
                        width: 24,
                        height: 24,
                        color: "#6b7280",
                        "&:hover": {
                          bgcolor: alpha("#0073ea", 0.08),
                          color: "#0073ea",
                        },
                      }}
                    >
                      <MoreHorizIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>

            {/* Search Bar - Show when search is active */}
            {isSearchActive && isFavoritesExpanded && (
              <Box className="search-container" sx={{ mt: 1.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search in favorites"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  autoFocus
                  className="search-input"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                      fontSize: '0.875rem',
                      bgcolor: '#f8f9fa',
                      '& fieldset': {
                        borderColor: '#e5e7eb',
                      },
                      '&:hover fieldset': {
                        borderColor: '#0073ea',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#0073ea',
                        borderWidth: '1px',
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 18, color: '#4b5563' }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => handleSearchChange('')}
                          sx={{ p: 0.5 }}
                        >
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            )}

            {/* Favorites List - Collapsible */}
            <Collapse in={isFavoritesExpanded} timeout="auto" unmountOnExit>
              <List disablePadding className="favorites-list">
                {filteredFavorites.map((favorite: FavoriteItem) => (
                  <ListItem key={favorite.id} disablePadding sx={{ mb: 0.5 }} className="favorite-item">
                    <ListItemButton
                      onClick={() => favorite.route && navigate(favorite.route)}
                      disabled={!favorite.route}
                      className={favorite.route && isActive(favorite.route) ? 'active' : ''}
                      sx={{
                        borderRadius: 1.5,
                        py: 0.75,
                        px: 1.5,
                        minHeight: 36,
                        bgcolor: favorite.route && isActive(favorite.route)
                          ? alpha("#0073ea", 0.12)
                          : "transparent",
                        "&:hover": {
                          bgcolor: favorite.route && isActive(favorite.route)
                            ? alpha("#0073ea", 0.18)
                            : alpha("#000", 0.04),
                        },
                        "&.Mui-disabled": {
                          opacity: 0.85,
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }} className="favorite-icon">
                        {React.cloneElement(getIconComponent(favorite.icon || 'Star'), {
                          sx: { 
                            color: favorite.route && isActive(favorite.route) 
                              ? "#0073ea" 
                              : favorite.color || "#4b5563",
                            fontSize: 18 
                          },
                        } as any)}
                      </ListItemIcon>
                      <ListItemText
                        primary={favorite.name}
                        slotProps={{
                          primary: {
                            sx: {
                              fontSize: "0.8125rem",
                              fontWeight: favorite.route && isActive(favorite.route) ? 600 : 500,
                              color: favorite.route && isActive(favorite.route) ? "#0073ea" : "#4b5563",
                            }
                          }
                        }}
                        className="favorite-text"
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>

              {/* Empty State for Search */}
              {isSearchActive && filteredFavorites.length === 0 && (
                <Box className="empty-state" sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    No favorites found
                  </Typography>
                </Box>
              )}

              {/* Empty State for No Favorites */}
              {!isSearchActive && (favorites || []).length === 0 && (
                <Box className="empty-state" sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    No favorites yet
                  </Typography>
                  <Button 
                    size="small" 
                    startIcon={<StarIcon />}
                    onClick={handleAddToFavorites}
                    sx={{ 
                      textTransform: 'none',
                      fontSize: '0.8rem',
                      color: '#0073ea',
                      '&:hover': {
                        backgroundColor: alpha('#0073ea', 0.08),
                      }
                    }}
                  >
                    Add current board
                  </Button>
                </Box>
              )}
            </Collapse>
          </Box>

          <Divider sx={{ my: 1 }} />

          {/* Workspaces Section */}
          <Box sx={{ px: 2, flex: 1, overflowY: "auto" }} className="workspaces-section">
            {/* Workspace Actions */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, mt: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "#4b5563",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  fontSize: "0.6875rem",
                }}
              >
                Workspaces
              </Typography>
              
              {/* Workspace Actions */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }} className="workspace-actions">
                <Tooltip title="Search workspaces">
                  <IconButton
                    size="small"
                    onClick={handleWorkspaceSearchToggle}
                    className="action-btn"
                    sx={{
                      width: 24,
                      height: 24,
                      color: "#6b7280",
                      "&:hover": {
                        bgcolor: alpha("#0073ea", 0.08),
                        color: "#0073ea",
                      },
                    }}
                  >
                    <Search sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Create new workspace">
                  <IconButton
                    size="small"
                    onClick={handleCreateWorkspaceModalOpen}
                    className="action-btn"
                    sx={{
                      width: 24,
                      height: 24,
                      color: "#6b7280",
                      "&:hover": {
                        bgcolor: alpha("#0073ea", 0.08),
                        color: "#0073ea",
                      },
                    }}
                  >
                    <AddIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Workspace Search Bar */}
            {isWorkspaceSearchActive && (
              <Box className="search-container" sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search workspaces and boards..."
                  value={workspaceSearchQuery}
                  onChange={(e) => handleWorkspaceSearchChange(e.target.value)}
                  autoFocus
                  className="search-input"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                      fontSize: '0.875rem',
                      bgcolor: '#f8f9fa',
                      '& fieldset': {
                        borderColor: '#e5e7eb',
                      },
                      '&:hover fieldset': {
                        borderColor: '#0073ea',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#0073ea',
                        borderWidth: '1px',
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 18, color: '#4b5563' }} />
                      </InputAdornment>
                    ),
                    endAdornment: workspaceSearchQuery && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => handleWorkspaceSearchChange('')}
                          sx={{ p: 0.5 }}
                        >
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            )}

            {/* Main Workspace Header */}
            <Box 
              className="workspace-header"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
                p: 1.5,
                backgroundColor: '#f8f9fa',
                border: '1px solid',
                borderColor: '#e5e7eb',
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: '#f1f5f9',
                  borderColor: '#0073ea',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 8px rgba(0, 115, 234, 0.15)',
                }
              }}
              onClick={() => navigate('/workspaces')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                {/* Workspace Icon */}
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 2,
                    backgroundColor: '#0073ea',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: 600,
                  }}
                >
                  <WorkOutline sx={{ fontSize: 18 }} />
                </Box>
                
                {/* Workspace Title and Info */}
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      color: "#1f2937",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                    }}
                  >
                    Workspaces
                  </Typography>
                  <Typography
                    sx={{
                      color: "#4b5563",
                      fontSize: "0.75rem",
                      mt: 0.5,
                    }}
                  >
                    {transformedWorkspaces.length} workspace{transformedWorkspaces.length !== 1 ? 's' : ''} • {transformedWorkspaces.reduce((acc, workspace) => acc + ((workspace.boards || []).length), 0)} boards
                  </Typography>
                </Box>
              </Box>
              
              {/* Add Workspace Button */}
              <Tooltip title="Add Workspace" placement="top">
                <IconButton
                  size="small"
                  onClick={handleCreateWorkspaceModalOpen}
                  className="add-workspace-btn"
                  sx={{
                    width: 32,
                    height: 32,
                    color: "#4b5563",
                    backgroundColor: 'white',
                    border: '1px solid',
                    borderColor: '#d1d5db',
                    '&:hover': {
                      bgcolor: "#0073ea",
                      color: "white",
                      borderColor: "#0073ea",
                      transform: 'scale(1.1)',
                    },
                  }}
                >
                  <AddIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Workspaces List with THREE DOT MENU */}
            <List disablePadding className="workspaces-list">
              {filteredWorkspaces.map((workspace, index) => {
                const workspaceKey = workspace.name.toLowerCase().replace(' ', '-');
                const isExpanded = expandedWorkspaces[workspaceKey];
                const reduxWorkspace = workspaces[index];

                return (
                  <Box key={workspaceKey} sx={{ mb: 1 }} className="workspace-item">
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        border: '1px solid',
                        borderColor: 'transparent',
                        borderRadius: 2,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: alpha("#000", 0.03),
                          borderColor: '#e5e7eb',
                        },
                      }}
                    >
                      <ListItemButton
                        onClick={() => toggleWorkspace(workspaceKey)}
                        sx={{
                          borderRadius: 2,
                          py: 1,
                          px: 1.5,
                          flex: 1,
                          border: 'none',
                        }}
                      >
                        {isExpanded ? (
                          <ExpandMore sx={{ fontSize: 18, color: "#6b7280", mr: 1 }} />
                        ) : (
                          <ChevronRight sx={{ fontSize: 18, color: "#6b7280", mr: 1 }} />
                        )}
                        <FiberManualRecord
                          sx={{ fontSize: 12, color: workspace.color, mr: 1.5 }}
                        />
                        <ListItemText
                          primary={workspace.name}
                          slotProps={{
                            primary: {
                              sx: {
                                fontWeight: 600,
                                fontSize: "0.875rem",
                                color: "#1f2937",
                              }
                            }
                          }}
                        />
                        <Chip
                          label={(workspace.boards || []).length}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.6875rem",
                            fontWeight: 600,
                            bgcolor: alpha(workspace.color, 0.1),
                            color: workspace.color,
                            "& .MuiChip-label": { px: 1 },
                          }}
                        />
                      </ListItemButton>

                      {/* THREE DOT MENU BUTTON */}
                      <Tooltip title="Workspace options">
                        <IconButton
                          size="small"
                          onClick={(e) => reduxWorkspace && handleWorkspaceMenuOpen(e, reduxWorkspace)}
                          sx={{
                            width: 32,
                            height: 32,
                            mr: 1,
                            color: '#6b7280',
                            '&:hover': {
                              color: '#0073ea',
                              bgcolor: alpha('#0073ea', 0.08),
                            },
                          }}
                        >
                          <MoreHorizIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <List disablePadding sx={{ pl: 4, mt: 0.5 }}>
                        {workspace.boards.map((board, boardIndex) => (
                          <ListItem key={boardIndex} disablePadding sx={{ mb: 0.5 }}>
                            <ListItemButton
                              onClick={() => board.route && navigate(board.route)}
                              disabled={!board.route}
                              sx={{
                                borderRadius: 1.5,
                                py: 0.75,
                                px: 1.5,
                                minHeight: 36,
                                bgcolor: board.route && isActive(board.route)
                                  ? alpha("#0073ea", 0.08)
                                  : "transparent",
                                border: '1px solid',
                                borderColor: 'transparent',
                                "&:hover": {
                                  bgcolor: board.route && isActive(board.route)
                                    ? alpha("#0073ea", 0.12)
                                    : alpha("#000", 0.03),
                                  borderColor: '#e5e7eb',
                                },
                                "&.Mui-disabled": {
                                  opacity: 0.85,
                                },
                              }}
                            >
                              <ListItemText
                                primary={board.name}
                                slotProps={{
                                  primary: {
                                    sx: {
                                      fontSize: "0.8125rem",
                                      fontWeight: board.route && isActive(board.route)
                                        ? 600
                                        : 500,
                                      color: board.route && isActive(board.route)
                                        ? "#0073ea"
                                        : "#4b5563",
                                    }
                                  }
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </Box>
                );
              })}
            </List>

            {/* Empty State for Workspace Search */}
            {isWorkspaceSearchActive && filteredWorkspaces.length === 0 && (
              <Box className="empty-state" sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  No workspaces found
                </Typography>
              </Box>
            )}

            {/* Empty State for No Workspaces */}
            {!isWorkspaceSearchActive && filteredWorkspaces.length === 0 && (
              <Box className="empty-state" sx={{ textAlign: 'center', py: 3 }}>
                <Box sx={{ mb: 2 }}>
                  <FolderIcon sx={{ fontSize: 48, color: '#d1d5db' }} />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  No workspaces yet
                </Typography>
                <Button 
                  size="small" 
                  startIcon={<AddIcon />}
                  onClick={handleCreateWorkspaceModalOpen}
                  sx={{ 
                    textTransform: 'none',
                    fontSize: '0.8rem',
                    color: '#0073ea',
                    '&:hover': {
                      backgroundColor: alpha('#0073ea', 0.08),
                    }
                  }}
                >
                  Create your first workspace
                </Button>
              </Box>
            )}
          </Box>

          <Divider />

          {/* Bottom Section */}
          <Box sx={{ px: 2, py: 2 }}>
            <List disablePadding>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => navigate("/settings")}
                  sx={{
                    borderRadius: 2,
                    py: 1,
                    px: 2,
                    bgcolor: isActive("/settings")
                      ? alpha("#0073ea", 0.08)
                      : "transparent",
                    "&:hover": {
                      bgcolor: isActive("/settings")
                        ? alpha("#0073ea", 0.12)
                        : alpha("#000", 0.04),
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <SettingsIcon
                      sx={{
                        color: isActive("/settings") ? "#0073ea" : "#4b5563",
                        fontSize: 20,
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary="Settings"
                    slotProps={{
                      primary: {
                        sx: {
                          fontWeight: isActive("/settings") ? 600 : 500,
                          fontSize: "0.875rem",
                          color: isActive("/settings") ? "#0073ea" : "#4b5563",
                        }
                      }
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </List>
          </Box>

          {/* Favorites Menu */}
          <Menu
            anchorEl={favoritesMenuAnchorEl}
            open={Boolean(favoritesMenuAnchorEl)}
            onClose={handleFavoritesMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            sx={{
              "& .MuiPaper-root": {
                borderRadius: 2,
                minWidth: 200,
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
                border: "1px solid",
                borderColor: "divider",
              },
            }}
            className="favorites-menu"
          >
            <MenuItem onClick={handleCreateNewFolder}>
              <ListItemIcon>
                <CreateNewFolderIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>New Folder</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleAddToFavorites}>
              <ListItemIcon>
                <StarIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Add to Favorites</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleFavoritesMenuClose}>
              <ListItemText>Manage Favorites</ListItemText>
            </MenuItem>
          </Menu>

          {/* Workspace THREE DOT MENU */}
          <Menu
            anchorEl={workspaceMenuAnchorEl}
            open={Boolean(workspaceMenuAnchorEl)}
            onClose={handleWorkspaceMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            sx={{
              "& .MuiPaper-root": {
                borderRadius: 2,
                minWidth: 200,
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
                border: "1px solid",
                borderColor: "divider",
              },
            }}
            className="workspace-menu"
          >
            <MenuItem onClick={handleEditWorkspace}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit Workspace</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleAddNewBoard}>
              <ListItemIcon>
                <DashboardIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>New Board</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { handleWorkspaceMenuClose(); navigate('/workspaces'); }}>
              <ListItemText>View All Workspaces</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Drawer>

      {/* Create Workspace Modal - Existing component */}
      <CreateWorkspaceModal 
        open={isCreateWorkspaceModalOpen} 
        onClose={handleCreateWorkspaceModalClose} 
      />

      {/* Edit Workspace Modal - Existing component */}
      <EditWorkspaceModal
        open={isEditWorkspaceModalOpen}
        onClose={handleEditWorkspaceModalClose}
        workspace={selectedWorkspace}
      />
    </>
  );
};